import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link as ReactRouterLink } from 'react-router-dom';
import { useInViewport } from 'react-in-viewport';
import {
  Box,
  Text,
  ScaleFade,
  LinkBox,
  LinkOverlay,
  Flex,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import PropTypes from 'prop-types';
import PublishTag from 'components/common/fields/PublishTag';
import AccessTag from 'components/common/fields/AccessTag';
import { useSelector } from 'react-redux';
import { ReducerStatus } from 'utils/constants';

import CourseImage from './CourseImage';
import ProgressDisplay from './ProgressDisplay';

function CourseItem({
  id,
  image,
  name,
  category,
  description,
  courseLink,
  isPublished,
  isPrivate,
  onClick,
  isAdminMode,
}) {
  const classProgressions = useSelector((state) => state.class.progressions);
  const classObject = useSelector((state) => state.class.class);
  const getClassProgressions = useSelector(
    (state) => state.class.getClassProgressions
  );

  const [progressLoading, setProgressLoading] = useState(true);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [subQuestCount, setSubQuestCount] = useState(0);

  const ref = useRef(null);

  const { inViewport } = useInViewport(
    ref,
    { rootMargin: '100%' },
    { disconnectOnLeave: false },
    {}
  );

  useEffect(() => {
    if (isAdminMode) return;
    const getProgress = async () => {
      try {
        const response = await axios.get(`/courses/${id}/progression`);
        const { totalSubQuestCount, progressions } = response.data;

        setProgressPercentage(
          (progressions.filter((p) => p.dateFinished).length /
            totalSubQuestCount) *
            100 || 0
        );

        setSubQuestCount(totalSubQuestCount);
      } catch {
        // If error occurs, set progress values to 0
        setProgressPercentage(0);
        setSubQuestCount(0);
      } finally {
        setProgressLoading(false);
      }
    };

    getProgress();
  }, [id, isAdminMode]);

  useEffect(() => {
    if (classProgressions && classObject && isAdminMode) {
      const { outlines, progressions } = classProgressions;

      const filteredProgressions = progressions.filter(
        (progression) => progression.courseId === id
      );

      const totalSubQuestCount = outlines.reduce(
        (accumulator, currentObject) => {
          if (currentObject.courseId === id) {
            return accumulator + currentObject.totalSubQuestCount;
          }
          return accumulator;
        },
        0
      );

      setProgressPercentage(
        (filteredProgressions.length /
          (totalSubQuestCount * classObject.users.length)) *
          100 || 0
      );
      setSubQuestCount(totalSubQuestCount);
      setProgressLoading(false);
    }
  }, [id, isAdminMode, classObject, classProgressions]);

  return (
    <Box
      w="100%"
      cursor="pointer"
      onClick={onClick}
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
    >
      <ScaleFade
        initialScale={0.9}
        in={inViewport}
        whileHover={{ scale: 1.01 }}
      >
        <LinkBox
          ref={ref}
          borderWidth="1px"
          borderRadius="sm"
          overflow="hidden"
          h="100%"
          position="relative"
        >
          <Box w="100%" h="100%">
            <CourseImage name={name} image={image} category={category} />
          </Box>

          <Box py={2} px={4} h="100%">
            <ProgressDisplay
              percentage={progressPercentage}
              count={subQuestCount}
              isLoading={
                progressLoading ||
                getClassProgressions.status === ReducerStatus.LOADING
              }
            />

            <Text fontSize="md" fontWeight="medium" colorScheme="gray">
              {isAdminMode ? (
                <LinkOverlay color="blue.500">{name}</LinkOverlay>
              ) : (
                <LinkOverlay
                  as={ReactRouterLink}
                  color="blue.500"
                  to={courseLink}
                >
                  {name}
                </LinkOverlay>
              )}

              {progressPercentage === 100 && (
                <CheckCircleIcon ml={2} color="green" boxSize={4} />
              )}
            </Text>

            {isAdminMode && (
              <Flex gap={2}>
                <PublishTag isPublished={isPublished} />
                <AccessTag isPrivate={isPrivate} />
              </Flex>
            )}

            <Text fontSize="sm">
              {description.length > 45
                ? `${description.substring(0, 45)}...`
                : description}
            </Text>
          </Box>
        </LinkBox>
      </ScaleFade>
    </Box>
  );
}

CourseItem.defaultProps = {
  image: null,
  isPublished: false,
  category: null,
  isPrivate: false,
  courseLink: '',
  onClick: () => {},
  isAdminMode: false,
};

CourseItem.propTypes = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  category: PropTypes.object,
  description: PropTypes.string.isRequired,
  courseLink: PropTypes.string,
  image: PropTypes.string,
  isPublished: PropTypes.bool,
  isPrivate: PropTypes.bool,
  onClick: PropTypes.func,
  isAdminMode: PropTypes.bool,
};

export default CourseItem;
